/* Collection description teaser: the <collection-description-read-more> element around the description and its
   Read more button (sections/main-collection-banner.liquid). Moved here from an inline script in the section
   (load path 2026-10-29) so the page parses no script ahead of the product grid; the markup renders collapsed on
   its own and the button stays hidden until this decides. Defined once however many banners a page prints. */
  if (!customElements.get('collection-description-read-more')) {
    customElements.define('collection-description-read-more', class extends HTMLElement {
      connectedCallback() {
        this.description = this.querySelector('.collection-description');
        this.button = this.querySelector(':scope > .collection-description-read-more');
        if (!this.description || this.observer) return;
        this.markBlankEdges();
        // A media-only description has no button: it shows as authored. The server decides this; the check here
        // only catches words the server could not see as blank (literal non-breaking spaces).
        if (this.button && !this.hasWords()) {
          this.description.removeAttribute('data-collapsed');
          this.description.setAttribute('data-media-only', '');
          this.button.remove();
          this.button = null;
        }
        if (!this.button) return;
        // Media, tables and embeds are set aside while collapsed, so Read more must stay reachable whenever the
        // description holds any, even when its words fit in three lines (AJ r6 item 8 review).
        this.hasSetAside = !!this.description.querySelector('img, picture, video, audio, iframe, object, embed, canvas');
        this.markEmpty();
        this.normalizeLeadingSpace();
        this.onToggle = () => {
          const expanded = this.button.getAttribute('aria-expanded') !== 'true';
          this.button.setAttribute('aria-expanded', String(expanded));
          this.description.toggleAttribute('data-collapsed', !expanded);
          this.button.textContent = expanded ? this.button.dataset.readLess : this.button.dataset.readMore;
          this.updateLinks();
        };
        this.button.addEventListener('click', this.onToggle);
        this.observer = new ResizeObserver(() => this.update());
        this.observer.observe(this.description);
        this.update();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.observer = null;
        this.button?.removeEventListener('click', this.onToggle);
      }

      update() {
        const expanded = this.button.getAttribute('aria-expanded') === 'true';
        if (!expanded) {
          const overflows = this.description.scrollHeight > this.description.clientHeight + 1;
          this.button.hidden = !overflows && !this.hasSetAside;
        }
        this.updateLinks();
      }

      // Leading and trailing children with no words and no media (an empty wrapper, a lone <br>, &nbsp;) are
      // editor leftovers. Marking them changes the expanded and media-only views; collapsed they take no space.
      markBlankEdges() {
        const media = 'img, picture, video, audio, iframe, svg, canvas, object, embed, table, hr, input, button, select, textarea';
        const isBlank = (el) => !el.textContent.replace(/[\s ​]/g, '') && !el.matches(media) && !el.querySelector(media);
        const children = [...this.description.children].filter((el) => !el.matches('style, script, meta, link, template'));
        for (const el of children) { if (!isBlank(el)) break; el.setAttribute('data-cq-blank', ''); }
        for (const el of children.reverse()) { if (!isBlank(el)) break; el.setAttribute('data-cq-blank', ''); }
      }

      // Blocks with no words and no media (an editor's &nbsp; paragraph, an empty heading) anywhere in the text.
      // They keep their authored spacing when expanded; the teaser sets them aside (CSS above). Inline elements
      // are left alone: a lone space in a <span> can be the only thing separating two words.
      markEmpty() {
        const media = 'img, picture, video, audio, iframe, svg, canvas, object, embed, table, hr, input, button, select, textarea';
        this.description.querySelectorAll('p, div, section, article, blockquote, h1, h2, h3, h4, h5, h6, li, dt, dd, td, th, center').forEach((el) => {
          if (!el.textContent.replace(/[\s\u00a0\u200b\ufeff]/g, '') && !el.querySelector(media)) el.setAttribute('data-cq-empty', '');
        });
      }

      hasWords() {
        const walker = document.createTreeWalker(this.description, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) {
          const node = walker.currentNode;
          if (node.parentElement.closest('style, script, noscript, template')) continue;
          if (node.textContent.replace(/[\s\u00a0\u200b\ufeff]/g, '')) return true;
        }
        return false;
      }

      // A non-breaking space the editor left before the first word (ahead of a video or an image) would indent the
      // teaser's first line. Whitespace-only text before the first word becomes an ordinary, collapsible space;
      // it sits beside media or inside blank lines, so the expanded view looks the same.
      normalizeLeadingSpace() {
        const walker = document.createTreeWalker(this.description, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) {
          const node = walker.currentNode;
          if (node.parentElement.closest('style, script, noscript, template, [data-cq-empty]')) continue;
          if (node.textContent.replace(/[\s\u00a0\u200b\ufeff]/g, '')) break;
          if (/[\u00a0\u200b\ufeff]/.test(node.textContent)) node.textContent = ' ';
        }
      }

      updateLinks() {
        const collapsed = this.description.hasAttribute('data-collapsed');
        const bottom = this.description.getBoundingClientRect().bottom;
        this.description.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          // Clipped below the three lines, or emptied by the teaser (a link around a set-aside image).
          const box = element.getBoundingClientRect();
          const clipped = collapsed && (box.bottom > bottom + 1 || box.width < 1 || box.height < 1);
          if (clipped && !element.hasAttribute('data-cq-original-tabindex')) {
            element.dataset.cqOriginalTabindex = element.getAttribute('tabindex') ?? '';
            element.setAttribute('tabindex', '-1');
          } else if (!clipped && element.hasAttribute('data-cq-original-tabindex')) {
            const original = element.dataset.cqOriginalTabindex;
            if (original === '') element.removeAttribute('tabindex');
            else element.setAttribute('tabindex', original);
            delete element.dataset.cqOriginalTabindex;
          }
        });
      }
    });
  }
